/* eslint-disable no-console */
/**
 * Prompt Input Component
 *
 * Text input field for user prompts with terminal-style styling.
 * Supports drag-and-drop image uploads for Solomon to process.
 * Shows image dimensions and suggested slot based on aspect ratio.
 */

import { useState, useRef, useEffect, useCallback } from 'react';

import { cn } from '@pray/shared/utils/styles';

import { useImageDropzone } from '../hooks/useImageDropzone';
import type { PromptInputProps } from '../types';
import {
  getImageDimensions,
  detectBestSlot,
  formatRatio,
  formatFileSize,
  type ImageDimensions,
  type SlotSuggestion,
} from '../utils/imageUtils';
import { parsePrompt } from '../utils/promptParser';

import styles from './PromptInput.module.scss';

// Send icon
function SendIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M22 2L11 13M22 2L15 22L11 13M22 2L2 9L11 13"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// Stop icon (square for stopping generation)
function StopIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect x="6" y="6" width="12" height="12" rx="2" />
    </svg>
  );
}

// Close icon for removing attached image
function CloseIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M18 6L6 18M6 6l12 12"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function PromptInput({
  value: controlledValue,
  defaultValue,
  onChange,
  onSubmit,
  onSubmitWithImage,
  onExecuteIndices,
  totalRecommendations = 0,
  placeholder = 'Enter a prompt or describe what you want...',
  isDisabled = false,
  isStreaming = false,
  onStop,
  className,
}: PromptInputProps) {
  const [internalValue, setInternalValue] = useState(defaultValue ?? '');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Message queue state - stores message to send after streaming completes
  const [queuedMessage, setQueuedMessage] = useState<string | null>(null);
  const [queuedImage, setQueuedImage] = useState<File | null>(null);

  // Track the last defaultValue we applied to avoid re-populating after user clears
  const lastAppliedDefaultRef = useRef<string | undefined>(defaultValue);
  // Track if user has manually submitted (to stop defaultValue from re-populating)
  const hasSubmittedRef = useRef(false);

  // Store stable references to callbacks for queued message effect
  const onSubmitRef = useRef(onSubmit);
  const onSubmitWithImageRef = useRef(onSubmitWithImage);
  onSubmitRef.current = onSubmit;
  onSubmitWithImageRef.current = onSubmitWithImage;

  // Image dimension and slot suggestion state
  const [imageDimensions, setImageDimensions] = useState<ImageDimensions | null>(null);
  const [slotSuggestion, setSlotSuggestion] = useState<SlotSuggestion | null>(null);

  // Image dropzone for drag-and-drop uploads
  const {
    droppedImage,
    imagePreviewUrl,
    isDragActive,
    clearDroppedImage,
    getRootProps,
    getInputProps,
    error: dropError,
  } = useImageDropzone(isDisabled);

  // Detect image dimensions when an image is dropped
  useEffect(() => {
    if (!droppedImage) {
      setImageDimensions(null);
      setSlotSuggestion(null);
      return;
    }

    getImageDimensions(droppedImage)
      .then((dims) => {
        setImageDimensions(dims);
        const suggestion = detectBestSlot(dims.ratio);
        setSlotSuggestion(suggestion);
      })
      .catch((err) => {
        console.error('Failed to get image dimensions:', err);
        setImageDimensions(null);
        setSlotSuggestion(null);
      });
  }, [droppedImage]);

  // Handler to clear image and related state
  const handleClearImage = useCallback(() => {
    clearDroppedImage();
    setImageDimensions(null);
    setSlotSuggestion(null);
  }, [clearDroppedImage]);

  // Update internal value when defaultValue changes (e.g., new image generation context)
  // Only apply if it's a genuinely new defaultValue AND user hasn't submitted yet
  useEffect(() => {
    // Only apply defaultValue if:
    // 1. It's defined and non-empty
    // 2. It's different from what we last applied
    // 3. User hasn't submitted yet (after submit, don't re-populate)
    if (defaultValue && defaultValue !== lastAppliedDefaultRef.current && !hasSubmittedRef.current) {
      setInternalValue(defaultValue);
      lastAppliedDefaultRef.current = defaultValue;
    }
  }, [defaultValue]);

  // Auto-submit queued message when streaming ends
  // Uses refs to avoid re-triggering when callbacks change
  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (!isStreaming && queuedMessage !== null) {
      // Small delay to ensure UI updates smoothly
      const timer = setTimeout(() => {
        console.log('[PromptInput] Auto-submitting queued message:', `${queuedMessage.substring(0, 50)}...`);
        if (queuedImage && onSubmitWithImageRef.current) {
          onSubmitWithImageRef.current(queuedMessage, queuedImage);
        } else if (onSubmitRef.current) {
          onSubmitRef.current(queuedMessage);
        }
        setQueuedMessage(null);
        setQueuedImage(null);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isStreaming, queuedMessage, queuedImage]);

  // Use controlled or uncontrolled value
  const value = controlledValue !== undefined ? controlledValue : internalValue;

  // Auto-resize textarea based on content
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = 'auto';
      // Set height to scrollHeight, capped at max-height (handled by CSS)
      textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
    }
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    if (controlledValue === undefined) {
      setInternalValue(newValue);
    }
    onChange?.(newValue);
  };

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (isDisabled) return;

    // Mark that user has submitted - prevents defaultValue from re-populating
    hasSubmittedRef.current = true;

    // If there's a dropped image, handle it
    if (droppedImage && onSubmitWithImage) {
      const promptText = value.trim() || 'Apply this image to my profile';

      // If streaming, queue the image submission
      if (isStreaming) {
        console.log('[PromptInput] Queueing message with image:', `${promptText.substring(0, 50)}...`);
        setQueuedMessage(promptText);
        setQueuedImage(droppedImage);
        clearDroppedImage();
        if (controlledValue === undefined) {
          setInternalValue('');
        }
        return;
      }

      // Not streaming - submit immediately
      onSubmitWithImage(promptText, droppedImage);
      clearDroppedImage();
      if (controlledValue === undefined) {
        setInternalValue('');
      }
      return;
    }

    // No text and no image - nothing to submit
    if (!value.trim()) return;

    // If streaming, queue the message instead of submitting
    if (isStreaming) {
      console.log('[PromptInput] Queueing message:', `${value.trim().substring(0, 50)}...`);
      setQueuedMessage(value.trim());
      if (controlledValue === undefined) {
        setInternalValue('');
      }
      return;
    }

    // Try to parse as a command (e.g., "1", "1 and 2", "all")
    if (totalRecommendations > 0 && onExecuteIndices) {
      const parsed = parsePrompt(value, totalRecommendations);

      if (parsed.type === 'selection' && parsed.indices && parsed.indices.length > 0) {
        onExecuteIndices(parsed.indices);
        // Clear internal value on submit
        if (controlledValue === undefined) {
          setInternalValue('');
        }
        return;
      }
    }

    // Fall back to regular prompt submission
    if (onSubmit) {
      onSubmit(value.trim());
      // Clear internal value on submit
      if (controlledValue === undefined) {
        setInternalValue('');
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Submit on Enter (without Shift), allow Shift+Enter for new lines
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  // Focus textarea on mount
  useEffect(() => {
    // Slight delay to allow dialog animation to complete
    const timer = setTimeout(() => {
      textareaRef.current?.focus();
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  const hasValue = value.trim().length > 0;
  const canSubmit = hasValue || !!droppedImage;

  // Get dropzone root props but don't add click handler (we use the hidden input)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
  const { onClick, ...dropzoneProps } = getRootProps();

  return (
    <div {...dropzoneProps} className={cn(styles.dropzoneContainer, isDragActive && styles.dragActive)}>
      {/* Hidden file input for dropzone */}
      <input {...getInputProps()} />

      {/* Drag overlay */}
      {isDragActive && (
        <div className={styles.dragOverlay}>
          <span>Drop image here</span>
        </div>
      )}

      {/* Image preview when file is dropped */}
      {imagePreviewUrl && droppedImage && (
        <div className={styles.imagePreview}>
          <div className={styles.previewThumbnail}>
            <img src={imagePreviewUrl} alt="Attached file preview" className={styles.previewImage} />
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleClearImage();
              }}
              className={styles.removeImage}
              aria-label="Remove attached image"
            >
              <CloseIcon />
            </button>
          </div>
          <div className={styles.previewInfo}>
            <span className={styles.fileName}>{droppedImage.name}</span>
            <span className={styles.fileDetails}>
              {imageDimensions ? `${imageDimensions.width} × ${imageDimensions.height} px` : 'Loading...'}
              {' • '}
              {formatFileSize(droppedImage.size)}
            </span>
            {slotSuggestion && (
              <span className={styles.slotSuggestion}>
                <span className={styles.ratioIcon}>📐</span>
                {formatRatio(imageDimensions?.ratio || 1)} → {slotSuggestion.label.split(' (')[0]}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Error message for rejected files */}
      {dropError && <div className={styles.dropError}>{dropError}</div>}

      {/* Queued message indicator */}
      {queuedMessage && (
        <div className={styles.queuedIndicator}>
          <span className={styles.queuedIcon}>⏳</span>
          <span className={styles.queuedText}>Message queued — will send when Solomon finishes</span>
        </div>
      )}

      <form className={cn(styles.container, isDisabled && styles.disabled, className)} onSubmit={handleSubmit}>
        <span className={styles.prompt}>&gt;</span>
        <textarea
          ref={textareaRef}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={
            // eslint-disable-next-line no-nested-ternary
            queuedMessage ? 'Message queued...' : imagePreviewUrl ? 'Describe how to use this image...' : placeholder
          }
          disabled={isDisabled}
          className={styles.input}
          aria-label="Enter your prompt"
          rows={1}
        />
        {/* Stop button when streaming, otherwise send button */}
        {isStreaming && onStop ? (
          <button
            type="button"
            onClick={onStop}
            className={cn(styles.submitButton, styles.stopButton)}
            aria-label="Stop generation"
          >
            <StopIcon />
          </button>
        ) : (
          <button
            type="submit"
            className={cn(styles.submitButton, canSubmit && styles.active)}
            disabled={!canSubmit || isDisabled}
            aria-label="Send prompt"
          >
            <SendIcon />
          </button>
        )}
      </form>
    </div>
  );
}

export default PromptInput;
